import {EventsEmitter, triggerStateChange} from '../events-emitter.js';
import {createElementWithProperties, WebElement, LitElement, css, defaultConverter} from "../custom-elements/abstract-element.js";
import {prepareEventsHandlers} from "./abstract-views.js";
import {parseUrl, Paths, updatePageUrlFromState} from "../util/url.js";
import {Store, store} from "../store.js";
import {GenericProvider} from "../providers/generic-provider.js";
import {notEqual} from "../libs/lit.dist.js";
import {CachedStoreModel} from "../util/cached-store-model.js";
export * from "../libs/lit.dist.js";

/**
 * Podstawowa klasa widoku-komponentu.
 * Zastępuje AbstractView.
 */
export class AbstractComponentView extends WebElement {

    /**
     * @type {Array<ProviderController>}
     * @private
     */
    __registeredProviders = [];
    updateOnStoreChange = false;

    /**
     * Wyświetla element we wskazanym elemencie HTML
     * @param {HTMLElement} container
     * @param {{}} props
     * @return {AbstractComponentView}
     */
    static showInContainer(container, props = {}) {
        this.registerComponent();
        const element = this.create(props);
        container.appendChild(element);
        return element;
    }

    /**
     * Tworzy instancję elementu HTML
     * @param props
     * @return {AbstractComponentView}
     */
    static create(props) {
        this.registerComponent();
        return createElementWithProperties(this.componentName, props);
    }

    constructor() {
        super();
        this.__paths = POLKURIER.paths
        this.__store = store;
        this.__events = new EventsEmitter();
        this.broadcastChannel = new BroadcastChannel('polkurier');
    }

    /**
     * Ustawia obiekt Store
     * @param {Store} store
     */
    set store(store) {
        if (store !== this.__store) {
            const previousStore = this.__store;
            if (this.__store) {
                this.unbindStoreEvents();
            }
            this.__store = store;
            if (this.__store && this.isConnected) {
                this.bindStoreEvents();
            }
            this.dispatchViewEvent('change:store', previousStore);
            this.requestUpdate();
        }
    }

    /**
     * Zwraca store
     * @return {Store}
     */
    get store() {
        return this.__store;
    }

    set events(events) {
        if (this.__events) {
            this.unbindEventsEmitterEvents();
        }
        this.__events = events;
        if (this.isConnected) {
            this.bindEventsEmitterEvents();
        }
    }

    get events() {
        return this.__events;
    }

    /**
     * Ustawia ścieżki do generowania URL
     * @param paths
     */
    set paths(paths) {
        this.__paths = Paths.factory(paths || {});
        this.requestUpdate();
    }

    /**
     * Zwraca ścieżki do generowania URL
     * @return {Paths}
     */
    get paths() {
        return this.__paths;
    }

    /**
     * Zwraca wartości właściwości zdefiniowanych w statycznym getterze `properties`
     * @see https://lit.dev/docs/components/properties/
     * @return {{}}
     */
    get props() {
        const props = {};
        for (let propertyName in this.constructor.properties) {
            props[propertyName] = this[propertyName];
        }
        return props;
    }

    /**
     * Zwraca wartości właściwości zdefiniowanych w statycznym getterze `properties`, które są zdefiniowane jako
     * 'internal reactive state' (mają parametr {state: true})
     * @see https://lit.dev/docs/components/properties/
     * @return {{}}
     */
    get stateProps() {
        const props = {};
        const properties = this.constructor.properties;
        for (let propertyName in properties) {
            if (properties[propertyName].state) {
                props[propertyName] = this[propertyName];
            }
        }
        return props;
    }

    /**
     * https://lit.dev/docs/components/lifecycle/#connectedcallback
     */
    connectedCallback() {
        super.connectedCallback();
        this.bindStateEvents();
        this.bindEventsEmitterEvents();
        this.bindStoreEvents();
        this.bindBroadcastChannelEvents();
    }

    /**
     * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
     */
    disconnectedCallback() {
        this.dispatchViewEvent('destroy');
        this.events.emit('destroy');
        super.disconnectedCallback();
        this.unbindStoreEvents();
        this.unbindEventsEmitterEvents();
        this.broadcastChannel = null;
    }

    requestUpdate(name, oldValue, options) {
        super.requestUpdate(name, oldValue, options);

        // Wysyła powiadomienia o zmianie stanu komponentu
        if (name !== void 0) {
            options ||= this.constructor.getPropertyOptions(name);
            const hasChanged = options.hasChanged || notEqual;
            if (hasChanged(this[name], oldValue)) {
                this.triggerStateChangeEvents(new Map([[name, oldValue]]));
            }
        }
    }

    /**
     * Wysyła powiadomienia o zmianie stanu komponentu (dowolnej wartości zarejestrowanej w properties):
     *  - 'syntetyczne' eventy po EventsEmitter
     *  - Eventy HTML (tylko dla siebie -- opcja bubbles: false)
     * @param {Map} changedProperties
     */
    triggerStateChangeEvents(changedProperties) {
        if (changedProperties.size > 0) {
            const properties = this.constructor.properties;
            const currentState = this.props;
            const previousState = {...currentState};
            for (let [propName, propValue] of changedProperties) {
                previousState[propName] = propValue;
            }

            this.dispatchViewEvent('pk:change:state', {currentState, previousState}, {bubble: false});
            let eventPath, eventProp;
            triggerStateChange((eventName, currentValue, previousValue) => {
                this.events.emit(eventName, currentValue, previousValue, currentState, previousState);
                if (eventName !== 'change') {
                    eventPath = eventName.split(':');
                    eventProp = eventPath[0] === 'change' ? properties[eventPath[1]] : null;
                    this.dispatchViewEvent(eventName, {
                        currentValue,
                        previousValue,
                        currentState,
                        previousState
                    }, {
                        bubbles: eventProp && eventProp.bubbleEvents === true,
                    });
                }
            }, currentState, previousState);
        }
    }

    /**
     * Zwraca eventy dla EventsEmitter w formacie {'nazwa:eventu': () => {}} np: {
     *     'change:user': () => this.requestUpdate()
     * }
     * @return {{}}
     */
    get eventsChannelEvents() {
        return {};
    }

    /**
     * Zwraca eventy dla store w formacie {'nazwa:eventu:store': () => {}} np: {
     *     'change:user': () => this.requestUpdate()
     * }
     * @return {{}}
     */
    get storeEvents() {
        return {};
    }

    /**
     * Zwraca eventy dla zmian stanu elementu {'change:propName:propField': () => {}} np: {
     *     'change:user:id': () => this.requestUpdate()
     * }
     * @return {{}}
     */
    get stateEvents() {
        return {};
    }

    /**
     * Definicja eventów BroadcastChannel w formacie {'nazwa:eventu': () => {}} np: {
     *     'change:user': () => this.requestUpdate()
     * }
     * @returns {Object}
     */
    get broadcastChannelEvents() {
        return {};
    }

    /**
     * Dodaje nasłuch eventów zmiany stanu elementu
     * @internal
     */
    bindStateEvents() {
        const events = prepareEventsHandlers(this.stateEvents);
        for (let eventName in events) {
            this.addEventListener(eventName, events[eventName]);
        }
    }

    /**
     * Dodaje nasłuch eventów STORE
     * @internal
     */
    bindStoreEvents() {
        if (this.store) {
            this.store.addEventListener(this, prepareEventsHandlers(this.storeEvents));
            if (this.updateOnStoreChange) {
                this.store.addEventListener(this, {
                    'change': () => {
                        this.requestUpdate();
                    }
                });
            }
        }
    }

    /**
     * Dodaje nasłuch dodatkowych eventów STORE
     * @param {{}} events
     * @return {AbstractComponentView}
     */
    listenToStore(events) {
        if (this.store) {
            this.store.addEventListener(this, events);
        }
        return this;
    }

    /**
     * Dodaje nasłuch eventu STORE
     * @param {String} type
     * @param {Function} handler
     * @return {AbstractComponentView}
     */
    addStoreEventListener(type, handler) {
        return this.listenToStore({[type]: handler});
    }

    /**
     * Usuwa nasłuch eventów STORE
     * @internal
     */
    unbindStoreEvents() {
        if (this.store) {
            this.store.removeEventListener(this);
        }
    }

    /**
     * Dodaje nasłuch eventów syntetycznych zdefiniowanych w eventsChannelEvents
     * @internal
     */
    bindEventsEmitterEvents() {
        this.addEventsEmitterEvents(this.eventsChannelEvents)
    }

    /**
     * Dodaje nasłuch eventów syntetycznych
     * @param {{}} events
     * @return {AbstractComponentView}
     */
    addEventsEmitterEvents(events) {
        this.events.addEventListener(this, prepareEventsHandlers(events));
        return this;
    }

    /**
     * Dodaje nasłuch eventu syntetycznego
     * @param {String} type
     * @param {Function} handler
     * @return {AbstractComponentView}
     */
    addEventsEmitterEvent(type, handler) {
        return this.addEventsEmitterEvents({[type]: handler});
    }

    /**
     * Usuwa nasłuch eventów
     * @internal
     */
    unbindEventsEmitterEvents() {
        this.events.removeEventListener(this);
    }

    /**
     * Dodaje nasłuch eventów z BroadcastChannel
     * @internal
     */
    bindBroadcastChannelEvents() {
        if (!this.broadcastChannel) {
            return;
        }
        const events = this.broadcastChannelEvents;
        this.broadcastChannel.onmessage = e => {
            const {type, ...data} = e.data;
            for (let eventType in events) {
                if (events.hasOwnProperty(eventType) && eventType === type) {
                    events[eventType](data);
                }
            }
        };
    }

    /**
     * Wywołuje zdarzenie HTML na elemencie
     * @param {String} eventName Nazwa eventu
     * @param {Object} detail Dodatkowe dane do przekazania @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent
     * @param {Object} options @see https://developer.mozilla.org/en-US/docs/Web/API/Event/Event
     */
    dispatchViewEvent(eventName, detail = {}, options = {}) {
        const event = new CustomEvent(eventName, {
            cancelable: true,
            bubbles: true,
            composed: true,
            ...options,
            detail,
        });
        this.dispatchEvent(event);
        return event;
    }

    /**
     * @param {String} type
     * @param {Function} listener
     * @param {Object|false} options
     * @return {AbstractComponentView}
     */
    addEventListener(type, listener, options = false) {
        super.addEventListener(type, listener, options);
        return this;
    }

    /**
     * Rejestruje providera danych
     * @param {GenericProvider} provider
     * @param {Boolean} loadOnFirstRender
     */
    registerDataProvider(provider, loadOnFirstRender = true) {
        if (provider instanceof GenericProvider && !this.isRegisteredDataProvider(provider)) {
            this.__registeredProviders.push(new ProviderController(this, provider, {
                load: loadOnFirstRender,
            }));
        }
        return provider;
    }

    /**
     * @internal
     * @param provider
     * @return {boolean}
     */
    isRegisteredDataProvider(provider) {
        for (let p of this.__registeredProviders) {
            if (p.provider === provider) {
                return true;
            }
        }
        return false;
    }

    /**
     * Zwraca HTML do wyświetlenia
     * @return {string|object}
     */
    template(templateContext) {
        throw `<${this.constructor.componentName}/> has no template`;
    }

    /**
     * Zwraca dane dla szablonu
     * @return {*}
     */
    templateContext() {
        return this;
    }

    /**
     * Wyświetla widok
     * @return {*}
     */
    render() {
        return this.template(this.templateContext());
    }

}

/**
 * @see https://lit.dev/docs/composition/controllers/
 */
export class StoreStateController {
    constructor(host, storeName = '', storeInstance = null) {
        this.host = host;
        this.storeName = storeName;
        this.__store = storeInstance || store;

        if (!storeInstance && host instanceof AbstractComponentView) {
            this.__store = () => host.store;
        }

        if (!this.storeName) {
            this.storeName = this.host.constructor.name + '__state';
        }
        host.addController(this);
    }

    get store() {
        if (typeof this.__store === 'function') {
            return this.__store();
        }
        return this.__store;
    }

    set(nextState) {
        this.store.merge(this.storeName, nextState);
        return this;
    }

    get(key) {
        const state = this.store.get(this.storeName, {});
        if (key !== undefined) {
            return state[key];
        }
        return state;
    }

    hostConnected() {
        this.host.addEventListener('change:store', e => {
            if (e.detail instanceof Store) {
                this.unbindStoreEvents(e.detail);
                this.bindStoreEvents();
            }
        });
        this.bindStoreEvents();
    }

    hostDisconnected() {
        this.unbindStoreEvents(this.store);
    }

    addPropertyChangeHandler(propertyName, handler) {
        this.store.addEventListener(this, {
            [`change:${this.storeName}:${propertyName}`]: handler,
        });
    }

    bindStoreEvents() {
        this.store.addEventListener(this, {
            [`change:${this.storeName}`]: () => this.host.requestUpdate(),
        });
    }

    unbindStoreEvents(store) {
        store.removeEventListener(this);
    }

}

/**
 * @see https://lit.dev/docs/composition/controllers/
 */
export class ProviderController {

    /**
     * @type GenericProvider
     */
    provider;

    /**
     * @type LitElement
     */
    host;

    static create(host, provider, options = {}) {
        new ProviderController(host, provider, options)
        return provider;
    }

    constructor(host, provider, options = {}) {
        if (!provider instanceof GenericProvider) {
            throw 'Provider must extend GenericProvider class'
        }
        this.host = host;
        this.provider = provider;
        this.options = options;
        host.addController(this);
    }

    hostUpdate() {
        if (this.options.load && !this.provider.isFetched() && !this.provider.isRequested()) {
            this.provider.load().then();
        }
    }

    hostConnected() {
        this.provider.store.addEventListener(this, this.provider.createStoreChangeEvents(() => this.host.requestUpdate(), true));
    }

    hostDisconnected() {
        this.provider.store.removeEventListener(this);
    }
}

/**
 * Ustawia stan komponentu to parametrów URL.
 *
 * Opcje konstruktora:
 *  – {Object|Function} state – Aktualny tan komponentu (Wymagany)
 *  – {Object|Function} defaultState – Domyślny stan komponentu – jeśli parametr w state różni się od domyślnego,
 *                                     to zostaje ustawiony do queryString w URL. **TYLKO PARAMETRY zdefiniowane
 *                                     w defaultState będą ustawiane w URL!** (Wymagany)
 *  – {Function} setUrlParams – Funkcja która ustawia parametry z url to komponentu. (Opcjonalne)
 *  – {Function} mapStateToUrl – Funkcja mapująca parametry ze stanu do URL (Opcjonalne)
 *  - {Function} mapUrlToState – Funkcja mapując parametry z URL do stanu (opcjonalne)
 *
 * Przykład użycia:
 * class MyComponent extends LitElement {
 *     constructor() {
 *         super();
 *         new UrlStateController(this, {
 *             state: () => ({
 *                 date: this.date,
 *             }),
 *             defaultState: {
 *                 date: getISODatePart(new Date()),
 *             }
 *         });
 *     }
 * }
 *
 *
 * @see https://lit.dev/docs/composition/controllers/
 */
export class UrlStateController {

    /**
     * @type LitElement
     */
    host;

    /**
     *
     * @param host
     * @param {{
     *  setUrlParams: Function|undefined,
     *  mapStateToUrl: Function|undefined,
     *  mapUrlToState: Function|undefined,
     *  defaultState: Object|Function|undefined,
     *  state: Object|Function|undefined,
     *  }} options
     */
    constructor(host, options) {
        this.options = options;
        this.options.mapStateToUrl ??= (props => props || {});
        this.options.mapUrlToState ??= (props => props || {});
        this.host = host;
        host.addController(this);
    }

    setHostUrlParams() {
        const urlParams = this.options.mapUrlToState(parseUrl(document.location.href).params);
        const defaultParams = this.defaultState;
        const currentState = this.state;
        const propertiesNames = Object.keys(defaultParams);
        const hostProperties = this.host.constructor.properties;
        if (typeof this.options.setUrlParams === 'function') {
            const params = {};
            for (let key of propertiesNames) {
                params[key] = urlParams[key] ?? currentState[key];
            }
            this.options.setUrlParams(params, currentState, defaultParams);
        } else {
            let propConverter, propValue;
            for (let key of propertiesNames) {
                if (typeof hostProperties[key]?.converter?.fromAttribute === 'function') {
                    propConverter = hostProperties[key].converter.fromAttribute;
                } else if (typeof hostProperties[key]?.converter === 'function') {
                    propConverter = hostProperties[key].converter;
                } else {
                    propConverter = defaultConverter.fromAttribute;
                }

                propValue = urlParams[key];
                if (typeof propValue === 'string') {
                    propValue = propConverter(urlParams[key], hostProperties[key].type);

                    if (typeof propValue === 'number' && isNaN(propValue)) {
                        propValue = undefined;
                    }
                }
                this.host[key] = propValue ?? currentState[key] ?? defaultParams[key];
            }
        }
    }

    get state() {
        if (!this.options.state) {
            return this.host.props || {};
        }
        return (typeof this.options.state === 'function' ? this.options.state() : this.options.state) || {};
    }

    get defaultState() {
        return (typeof this.options.defaultState === 'function' ? this.options.defaultState() : this.options.defaultState) || {};
    }

    updateUrl() {
        const state = {};
        const currentState = this.state;
        const defaultState = this.defaultState;
        for (let key of Object.keys(defaultState)) {
            state[key] = currentState[key];
        }
        updatePageUrlFromState(state, defaultState, this.options.mapStateToUrl, this.options.mapUrlToState);
    }

    hostUpdated() {
        this.updateUrl();
    }

    hostConnected() {
        this.setHostUrlParams();
        this.onPopState = () => {
            this.setHostUrlParams();
        }
        window.addEventListener('popstate', this.onPopState);
    }

    hostDisconnected() {
        window.removeEventListener('popstate', this.onPopState);
        this.onPopState = null;
    }

}

/**
 * Dodaje ścieżki zdefiniowane w HTML do obiektu `paths` w komponencie.
 *
 * Przykład użycia:
 * class MyComponent extends LitElement {
 *     constructor() {
 *         super();
 *         new PathsAttributeController(this);
 *     }
 * }
 *
 * I później w drzewie HTML:
 * <pk-my-component>
 *     <pk-path name="route_name" value="/route/url"></pk-path>
 *     <pk-path name="other_route_name" value="/some_other/url"></pk-path>
 * </pk-my-component>
 *
 * lub jako atrybut:
 * <pk-my-component
 *  paths:route_name="/route/url"
 *  paths:other_route_name="/some_other/url"
 * ></<pk-my-component>>
 *
 * @see https://lit.dev/docs/composition/controllers/
 */
export class PathsAttributeController {

    /**
     * @type LitElement
     */
    host;
    prefix = 'paths:';

    constructor(host) {
        this.host = host;
        host.addController(this);
        this.mutationObserver = new MutationObserver(this.collectPaths.bind(this));
    }

    collectPaths() {
        const paths = {};
        for (let attr of this.host.attributes) {
            if (attr.nodeName.includes(this.prefix)) {
                paths[attr.nodeName.substring(this.prefix.length)] = attr.nodeValue;
            }
        }

        for (let el of this.host.querySelectorAll('pk-path')) {
            if (el?.attributes?.name?.value) {
                paths[el.attributes.name.value] = el.attributes?.value?.value || '';
            }
        }
        this.host.paths?.add(paths);
    }

    hostConnected() {
        this.mutationObserver.observe(this.host, {
            attributes: true,
            childList: true,
        });
        this.collectPaths();
    }

    hostDisconnected() {
        this.mutationObserver.disconnect();
    }
}

window.customElements.define('pk-path', class extends LitElement {
    static styles = css`
        :host {
            display: none;
        }
    `;
});

export class IntervalController {
    constructor(host, callback, timeout) {
        this.host = host;
        this.callback = callback;
        this.timeout = timeout;
        host.addController(this);
    }
    hostConnected() {
        this.__interval = setInterval(this.callback, this.timeout);
    }
    hostDisconnected() {
        clearInterval(this.__interval);
    }
}

export class CachedStoreModelController {

    /**
     * @type CachedStoreModel
     */
    cachedModel;

    /**
     * @type LitElement
     */
    host;

    static create(host, namespace, options = {}) {
        const cachedModel = new CachedStoreModel(host.store, namespace, {
            ...options,
            initialize: false,
            onChange: (...arg) => {
                options.onChange?.(...arg);
                host.requestUpdate();
            },
        });
        new CachedStoreModelController(host, cachedModel);
        return cachedModel;
    }

    constructor(host, cachedModel) {
        if (!cachedModel instanceof CachedStoreModel) {
            throw 'Provider must extend CachedStoreModel class'
        }
        this.host = host;
        this.cachedModel = cachedModel;
        host.addController(this);
    }

    hostConnected() {
        this.cachedModel.initialize();
    }

    hostDisconnected() {
        this.cachedModel.destroy();
    }
}

export class ExternalEventListenerController {

    /**
     * @type LitElement
     */
    host;
    isConnected = false;
    externalEventsHandlers = [];

    constructor(host) {
        host.addController(this);
    }

    addEventListener(target, event, callback) {
        if (this.isConnected) {
            target.addEventListener(event, callback);
        }
        this.externalEventsHandlers.push({event, target, callback});
        return callback;
    }

    hostConnected() {
        this.isConnected = true;
        for (let handler of this.externalEventsHandlers) {
            handler.target.addEventListener(handler.event, handler.callback);
        }
    }

    hostDisconnected() {
        this.isConnected = false;
        for (let handler of this.externalEventsHandlers) {
            handler.target.removeEventListener(handler.event, handler.callback);
        }
    }
}
